(() => {
  const countdownDateTimeFormatters = new Map();
  const COUNTDOWN_DAY_IN_MILLISECONDS = 86400000;

  function getCountdownDateTimeFormatter(timeZone) {
    if (!countdownDateTimeFormatters.has(timeZone)) {
      countdownDateTimeFormatters.set(
        timeZone,
        new Intl.DateTimeFormat("en-CA", {
          timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hourCycle: "h23",
        }),
      );
    }

    return countdownDateTimeFormatters.get(timeZone);
  }

  function getCountdownZonedParts(date, timeZone) {
    const parts = {};

    for (const part of getCountdownDateTimeFormatter(timeZone).formatToParts(
      date,
    )) {
      if (part.type !== "literal") {
        parts[part.type] = Number(part.value);
      }
    }

    return parts;
  }

  function parseCountdownTime(value) {
    const match = value?.trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (!match) return null;

    return {
      hour: Number(match[1]),
      minute: Number(match[2]),
    };
  }

  function parseCountdownDatasetString(value) {
    try {
      const parsedValue = JSON.parse(value);
      return typeof parsedValue === "string" ? parsedValue : "";
    } catch {
      return value || "";
    }
  }

  function getCountdownDateKey({ year, month, day }) {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  function addCountdownCivilDays(dateParts, days) {
    const date = new Date(
      Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day) +
        days * COUNTDOWN_DAY_IN_MILLISECONDS,
    );

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }

  function getCountdownWeekday(dateParts) {
    return new Date(
      Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day),
    ).getUTCDay();
  }

  function countdownZonedDateTimeToTimestamp(dateParts, time, timeZone) {
    const desiredTimestamp = Date.UTC(
      dateParts.year,
      dateParts.month - 1,
      dateParts.day,
      time.hour,
      time.minute,
    );
    let timestamp = desiredTimestamp;

    for (let attempt = 0; attempt < 4; attempt++) {
      const zonedParts = getCountdownZonedParts(new Date(timestamp), timeZone);
      const representedTimestamp = Date.UTC(
        zonedParts.year,
        zonedParts.month - 1,
        zonedParts.day,
        zonedParts.hour,
        zonedParts.minute,
      );
      const adjustment = desiredTimestamp - representedTimestamp;

      if (adjustment === 0) return timestamp;
      timestamp += adjustment;
    }

    const finalParts = getCountdownZonedParts(new Date(timestamp), timeZone);
    const isExactMatch =
      finalParts.year === dateParts.year &&
      finalParts.month === dateParts.month &&
      finalParts.day === dateParts.day &&
      finalParts.hour === time.hour &&
      finalParts.minute === time.minute;

    return isExactMatch ? timestamp : null;
  }

  function parseCountdownDateOverrides(value) {
    const overrides = new Map();

    for (const line of value?.split(/\r?\n/) ?? []) {
      const match = line
        .trim()
        .match(/^(\d{4})-(\d{2})-(\d{2})\s+([01]\d|2[0-3]):([0-5]\d)$/);
      if (!match) continue;

      const dateParts = {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
      };
      const normalizedDate = new Date(
        Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day),
      );
      const isValidDate =
        normalizedDate.getUTCFullYear() === dateParts.year &&
        normalizedDate.getUTCMonth() + 1 === dateParts.month &&
        normalizedDate.getUTCDate() === dateParts.day;

      if (!isValidDate) continue;

      overrides.set(getCountdownDateKey(dateParts), {
        dateParts,
        time: {
          hour: Number(match[4]),
          minute: Number(match[5]),
        },
      });
    }

    return overrides;
  }

  function getNextCountdownTarget(now, schedule) {
    const timeZone = schedule.timeZone || "Europe/Copenhagen";
    const overrides = parseCountdownDateOverrides(schedule.dateOverrides);
    const overriddenDateKeys = new Set(overrides.keys());
    const candidates = [];
    const specificTarget = Number(schedule.specificTarget);
    let specificDateKey = null;

    if (Number.isFinite(specificTarget)) {
      specificDateKey = getCountdownDateKey(
        getCountdownZonedParts(new Date(specificTarget), timeZone),
      );
      overriddenDateKeys.add(specificDateKey);

      if (specificTarget > now.getTime()) {
        candidates.push(specificTarget);
      }
    }

    for (const [dateKey, override] of overrides) {
      if (dateKey === specificDateKey) continue;

      const timestamp = countdownZonedDateTimeToTimestamp(
        override.dateParts,
        override.time,
        timeZone,
      );
      if (timestamp !== null && timestamp > now.getTime()) {
        candidates.push(timestamp);
      }
    }

    const currentZonedParts = getCountdownZonedParts(now, timeZone);
    const currentDateParts = {
      year: currentZonedParts.year,
      month: currentZonedParts.month,
      day: currentZonedParts.day,
    };

    for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
      const dateParts = addCountdownCivilDays(currentDateParts, dayOffset);
      if (overriddenDateKeys.has(getCountdownDateKey(dateParts))) continue;

      const time = parseCountdownTime(
        schedule.weekdayTimes?.[getCountdownWeekday(dateParts)],
      );
      if (!time) continue;

      const timestamp = countdownZonedDateTimeToTimestamp(
        dateParts,
        time,
        timeZone,
      );
      if (timestamp !== null && timestamp > now.getTime()) {
        candidates.push(timestamp);
      }
    }

    return candidates.length > 0 ? Math.min(...candidates) : null;
  }

  if (
    typeof customElements !== "undefined" &&
    !customElements.get("custom-countdown")
  ) {
    class CustomCountdown extends HTMLElement {
      constructor() {
        super();
        window.initScriptOnDemand(this, this.init.bind(this));

        if (this.dataset.loading === "eager") {
          this.init();
        }
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        this.isRecurring = this.dataset.recurring === "true";
        this.schedule = {
          timeZone: this.dataset.timeZone,
          dateOverrides: parseCountdownDatasetString(
            this.dataset.dateOverrides,
          ),
          specificTarget: new Date(this.dataset.endDate).getTime(),
          weekdayTimes: [
            this.dataset.sundayTime,
            this.dataset.mondayTime,
            this.dataset.tuesdayTime,
            this.dataset.wednesdayTime,
            this.dataset.thursdayTime,
            this.dataset.fridayTime,
            this.dataset.saturdayTime,
          ],
        };
        this.endDate = this.isRecurring
          ? getNextCountdownTarget(new Date(), this.schedule)
          : new Date(this.dataset.endDate).getTime();

        if (!Number(this.endDate)) {
          this.hidden = true;
          return;
        }

        this.secondsElem = this.querySelector(".js-seconds");
        this.minutesElem = this.querySelector(".js-minutes");
        this.hoursElem = this.querySelector(".js-hours");
        this.daysElem = this.querySelector(".js-days");

        this.seconds = 1000;
        this.minutes = this.seconds * 60;
        this.hours = this.minutes * 60;
        this.days = this.hours * 24;

        this.nextTick();
        this.intervalTimer = setInterval(
          this.nextTick.bind(this),
          this.seconds,
        );
      }

      nextTick() {
        const now = new Date();
        let remaining = this.endDate - now;

        if (this.isRecurring && remaining <= 0) {
          this.endDate = getNextCountdownTarget(now, this.schedule);
          if (!Number(this.endDate)) {
            clearInterval(this.intervalTimer);
            this.hidden = true;
            return;
          }
          remaining = this.endDate - now;
        }

        if (remaining < 0) {
          clearInterval(this.intervalTimer);
          return;
        }

        const days = Math.floor(remaining / this.days);
        const hours = Math.floor(remaining / this.hours);
        const minutes = Math.floor(remaining / this.minutes);
        const secs = Math.floor(remaining / this.seconds);

        // Recurring pickup countdowns express the remaining time in hours
        // (which may exceed 24, e.g. "44:59:00") instead of rolling over into a
        // separate days unit. Date-based countdowns keep the days unit.
        const collapseDaysIntoHours = this.isRecurring;

        let digits = 2;
        const remainingDays = collapseDaysIntoHours ? 0 : days;
        const remainingHours = collapseDaysIntoHours ? hours : hours - days * 24;
        const remainingMinutes = minutes - hours * 60;
        const remainingSecs = secs - minutes * 60;
        const showDays = remainingDays > 0;
        const showHours = collapseDaysIntoHours || showDays || remainingHours > 0;

        if (this.daysElem) {
          this.daysElem.closest(".countdown__block").hidden = !showDays;
          this.daysElem.textContent =
            remainingDays < 10 ? `0${remainingDays}` : remainingDays;
          if (showDays) digits++;
        }

        if (this.hoursElem) {
          this.hoursElem.closest(".countdown__block").hidden = !showHours;
          this.hoursElem.textContent =
            remainingHours < 10 ? `0${remainingHours}` : remainingHours;
          if (showHours) digits++;
        }

        this.minutesElem.textContent =
          remainingMinutes < 10 ? `0${remainingMinutes}` : remainingMinutes;
        this.secondsElem.textContent =
          remainingSecs < 10 ? `0${remainingSecs}` : remainingSecs;

        this.setAttribute("data-digits", digits);
      }
    }

    customElements.define("custom-countdown", CustomCountdown);
  }

  if (typeof module !== "undefined") {
    module.exports = {
      countdownZonedDateTimeToTimestamp,
      getNextCountdownTarget,
      parseCountdownDateOverrides,
      parseCountdownDatasetString,
      parseCountdownTime,
    };
  }
})();
